import { useMemo } from "react";
import { motion } from "framer-motion";

interface ConfettiProps {
  /** Number of pieces */
  count?: number;
  /** Show/hide */
  active?: boolean;
}

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--primary) / 0.6)",
  "hsl(var(--foreground) / 0.35)",
  "hsl(var(--secondary))",
];

/**
 * Full-screen confetti falling from the top of the viewport.
 * Purely decorative — pointer-events none, sits above dialogs.
 */
export const Confetti = ({ count = 90, active = true }: ConfettiProps) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 2.6 + Math.random() * 2.2,
        drift: (Math.random() - 0.5) * 160,
        size: 5 + Math.random() * 7,
        rotate: Math.random() * 720 - 360,
        color: COLORS[i % COLORS.length],
        round: Math.random() > 0.7,
      })),
    [count],
  );

  if (!active) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[100] overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          initial={{ y: "-12vh", x: 0, opacity: 0, rotate: 0 }}
          animate={{ y: "110vh", x: p.drift, opacity: [0, 1, 1, 0.9], rotate: p.rotate }}
          transition={{ duration: p.duration, delay: p.delay, ease: "linear" }}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: 0,
            width: p.size,
            height: p.round ? p.size : p.size * 2.2,
            backgroundColor: p.color,
            borderRadius: p.round ? "9999px" : 0,
          }}
        />
      ))}
    </div>
  );
};

export default Confetti;
